/**
 * useResizeHandles Hook (Task Group 4)
 *
 * Custom hook for handling tool block resize via edge dragging.
 *
 * Features:
 * - Right edge drag: changes duration only (startTime stays fixed)
 * - Left edge drag: changes startTime AND duration (endTime stays fixed)
 * - Snap-to-grid at 0.1s intervals
 * - Minimum duration enforcement (0.1s)
 * - Edge zone detection for cursor changes
 * - Real-time preview dimensions during drag
 */

import { useState, useCallback, useRef } from 'react';
import { snapToGrid, GRID_INTERVAL } from '../utils/snapToGrid';
import type { Tool } from '../../../stores/timelineStore';

/**
 * Edge zone type for resize operations
 */
export type ResizeEdge = 'left' | 'right' | null;

/**
 * Threshold in pixels for edge detection
 * User must be within this distance from the edge to trigger resize cursor
 * Set wider (14px) to make it easier to grab edges even with hover effects
 */
export const EDGE_THRESHOLD = 14;

/**
 * Minimum duration in seconds for tool blocks
 * Prevents tools from being resized to invisibly small sizes
 */
export const MIN_DURATION = 0.1;

/**
 * Props for the useResizeHandles hook
 */
export interface UseResizeHandlesProps {
  /**
   * Tool data from timeline store
   */
  tool: Tool;

  /**
   * Pixels per second for time/pixel conversion
   */
  pixelsPerSecond: number;

  /**
   * Callback when resize completes with new values
   */
  onUpdate: (updates: { startTime: number; duration: number }) => void;
}

/**
 * Return type for the useResizeHandles hook
 */
export interface UseResizeHandlesReturn {
  /**
   * Whether a resize operation is currently in progress
   */
  isResizing: boolean;

  /**
   * Which edge is currently being dragged (null if not resizing)
   */
  resizeEdge: ResizeEdge;

  /**
   * Preview dimensions during resize (for visual feedback)
   */
  previewDimensions: {
    startTime: number;
    duration: number;
  } | null;

  /**
   * Detect which edge zone the mouse is in
   * @param localX - X position relative to tool block left edge
   * @param width - Total width of the tool block
   * @returns 'left', 'right', or null if not in an edge zone
   */
  getEdgeZone: (localX: number, width: number) => ResizeEdge;

  /**
   * Handle mouse down to start resize
   * @param edge - Which edge was clicked
   * @param clientX - Mouse X position in pixels (absolute)
   */
  handleMouseDown: (edge: ResizeEdge, clientX: number) => void;

  /**
   * Handle mouse move during resize
   * @param clientX - Current mouse X position in pixels
   */
  handleMouseMove: (clientX: number) => void;

  /**
   * Handle mouse up to end resize
   */
  handleMouseUp: () => void;

  /**
   * Cancel resize operation without applying changes
   */
  cancelResize: () => void;
}

/**
 * Custom hook for handling tool block resize operations
 *
 * Implements edge-based resizing with the following behaviors:
 * - Right edge: Extends or shrinks duration, startTime stays fixed
 * - Left edge: Moves startTime and adjusts duration to keep endTime fixed
 *
 * @param props - Hook configuration
 * @returns Resize state and handlers
 *
 * @example
 * ```tsx
 * const resize = useResizeHandles({
 *   tool,
 *   pixelsPerSecond: 100,
 *   onUpdate: (updates) => timelineStore.updateTool(tool.id, updates),
 * });
 *
 * // In component
 * <div
 *   style={{ cursor: resize.isResizing ? 'ew-resize' : 'move' }}
 *   onMouseDown={(e) => {
 *     const edge = resize.getEdgeZone(localX, width);
 *     if (edge) resize.handleMouseDown(edge, e.clientX);
 *   }}
 * />
 * ```
 */
export function useResizeHandles({
  tool,
  pixelsPerSecond,
  onUpdate,
}: UseResizeHandlesProps): UseResizeHandlesReturn {
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<ResizeEdge>(null);
  const [previewDimensions, setPreviewDimensions] = useState<{
    startTime: number;
    duration: number;
  } | null>(null);

  // Track original values when drag started
  const dragStartRef = useRef<{
    edge: ResizeEdge;
    startX: number;
    originalStartTime: number;
    originalDuration: number;
    originalEndTime: number;
  } | null>(null);

  /**
   * Detect which edge zone the mouse is in
   */
  const getEdgeZone = useCallback(
    (localX: number, width: number): ResizeEdge => {
      // Check left edge zone (within EDGE_THRESHOLD pixels of left edge)
      if (localX <= EDGE_THRESHOLD) {
        return 'left';
      }

      // Check right edge zone (within EDGE_THRESHOLD pixels of right edge)
      if (localX >= width - EDGE_THRESHOLD) {
        return 'right';
      }

      return null;
    },
    []
  );

  /**
   * Start resize operation
   */
  const handleMouseDown = useCallback(
    (edge: ResizeEdge, clientX: number) => {
      if (!edge) return;

      const originalEndTime = tool.startTime + tool.duration;

      dragStartRef.current = {
        edge,
        startX: clientX,
        originalStartTime: tool.startTime,
        originalDuration: tool.duration,
        originalEndTime,
      };

      setIsResizing(true);
      setResizeEdge(edge);
      setPreviewDimensions({
        startTime: tool.startTime,
        duration: tool.duration,
      });
    },
    [tool.startTime, tool.duration]
  );

  /**
   * Handle mouse move during resize
   */
  const handleMouseMove = useCallback(
    (clientX: number) => {
      if (!isResizing || !dragStartRef.current) return;

      const {
        edge,
        startX,
        originalStartTime,
        originalDuration,
        originalEndTime,
      } = dragStartRef.current;

      // Calculate pixel delta from drag start
      const deltaX = clientX - startX;

      let newStartTime: number;
      let newDuration: number;

      if (edge === 'right') {
        // Right edge drag: change duration only, startTime stays fixed
        // New duration = original + delta converted to time
        const deltaDuration = deltaX / pixelsPerSecond;
        newDuration = snapToGrid(originalDuration + deltaDuration);

        // Enforce minimum duration
        newDuration = Math.max(MIN_DURATION, newDuration);

        // startTime unchanged
        newStartTime = originalStartTime;
      } else {
        // Left edge drag: change startTime AND duration, endTime stays fixed
        // New startTime = original + delta converted to time
        const deltaTime = deltaX / pixelsPerSecond;
        newStartTime = snapToGrid(originalStartTime + deltaTime);

        // Enforce startTime >= 0
        newStartTime = Math.max(0, newStartTime);

        // Calculate new duration to keep endTime fixed
        newDuration = originalEndTime - newStartTime;

        // Enforce minimum duration (adjust startTime if needed)
        if (newDuration < MIN_DURATION) {
          newDuration = MIN_DURATION;
          newStartTime = originalEndTime - MIN_DURATION;
          // Re-snap startTime after adjustment
          newStartTime = snapToGrid(newStartTime, GRID_INTERVAL);
        }
      }

      setPreviewDimensions({
        startTime: newStartTime,
        duration: newDuration,
      });
    },
    [isResizing, pixelsPerSecond]
  );

  /**
   * End resize operation and apply changes
   */
  const handleMouseUp = useCallback(() => {
    if (!isResizing || !dragStartRef.current || !previewDimensions) {
      setIsResizing(false);
      setResizeEdge(null);
      setPreviewDimensions(null);
      dragStartRef.current = null;
      return;
    }

    const { originalStartTime, originalDuration } = dragStartRef.current;

    // Only call onUpdate if values actually changed
    const startTimeChanged =
      Math.abs(previewDimensions.startTime - originalStartTime) > 0.001;
    const durationChanged =
      Math.abs(previewDimensions.duration - originalDuration) > 0.001;

    if (startTimeChanged || durationChanged) {
      onUpdate({
        startTime: previewDimensions.startTime,
        duration: previewDimensions.duration,
      });
    }

    // Reset state
    setIsResizing(false);
    setResizeEdge(null);
    setPreviewDimensions(null);
    dragStartRef.current = null;
  }, [isResizing, previewDimensions, onUpdate]);

  /**
   * Cancel resize without applying changes
   */
  const cancelResize = useCallback(() => {
    setIsResizing(false);
    setResizeEdge(null);
    setPreviewDimensions(null);
    dragStartRef.current = null;
  }, []);

  return {
    isResizing,
    resizeEdge,
    previewDimensions,
    getEdgeZone,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    cancelResize,
  };
}
